/**
 * catalogInvalidation — "the list's server-resolved labels are stale; re-ask" (F-40 / IM-10).
 *
 * `display.model` and `display.style` are resolved SERVER-side on every list row
 * (`service.py::display_fields`). A `PATCH /api/catalog/phone-models/{id}` therefore changes
 * them for every template that references the model, and the only correct client response is
 * to re-ask the server — there is nothing the client could recompute. `CatalogAdmin` already
 * did exactly this (`await refresh()` after its save), which is why that surface was never
 * affected; the fix is to give the same signal to the surfaces that lacked it, so fixing a
 * typo from inside the editor or the wizard no longer leaves the list painting the old string
 * until a manual refresh or the next 60s tick.
 *
 * Deliberately the same five-line module-level subscriber-set shape as
 * `sync/autoSync.subscribeTemplates` (`autoSync.ts:393-400`), so the codebase has ONE pattern
 * for this and not two.
 */

/** What changed. `template` covers "a template was created or edited elsewhere in this app"
 *  — the wizard finishing, the mask editor closing — which invalidates the list for the same
 *  reason: the row set or a row's fields are no longer what was last fetched. */
export type CatalogChangeKind = "phoneModel" | "caseStyle" | "template";

type Subscriber = (kind: CatalogChangeKind) => void;

const subscribers = new Set<Subscriber>();

/** Tell every mounted template list that its server-resolved labels are stale. */
export function notifyCatalogChanged(kind: CatalogChangeKind): void {
  for (const cb of [...subscribers]) {
    try {
      cb(kind);
    } catch {
      /* a subscriber throw must never break the save that triggered it, nor starve the
         other subscribers — same contract as autoSync's notify loop */
    }
  }
}

/** Subscribe to catalog changes. Returns an unsubscribe fn. */
export function subscribeCatalogChanged(cb: Subscriber): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/** Test-only: drop every subscriber so one test's mount cannot leak into the next. */
export function __resetCatalogInvalidationForTests(): void {
  subscribers.clear();
}
